import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from './utils';

const buttonVariants = cva(
  'relative inline-flex min-h-11 select-none items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 text-[15px] font-medium transition-[background-color,border-color,box-shadow,filter,transform,color] duration-150 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-45 [&_svg]:size-[17px] [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-brand text-brand-ink shadow-[inset_0_1px_0_rgb(255_255_255/0.2),0_10px_20px_-10px_color-mix(in_oklab,var(--brand)_85%,transparent)] hover:brightness-110',
        secondary:
          'border border-line bg-panel text-ink shadow-[0_1px_2px_rgb(40_44_110/0.05)] hover:border-muted/35 hover:bg-soft',
        soft: 'bg-brand-soft text-brand-2 hover:brightness-[0.97]',
        ghost: 'text-ink hover:bg-soft',
        danger:
          'bg-gap text-white shadow-[0_10px_20px_-10px_color-mix(in_oklab,var(--gap)_80%,transparent)] hover:brightness-110',
        'danger-soft': 'bg-gap/10 text-gap hover:bg-gap/15',
        link: 'min-h-0 px-0 text-brand underline-offset-4 hover:underline',
      },
      size: {
        default: '',
        sm: 'min-h-9 px-3.5 text-sm [&_svg]:size-[15px]',
        lg: 'min-h-12 px-7 text-base',
        icon: 'size-11 px-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild, type, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
